import { CommonModule } from '@angular/common'
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    model,
    output,
    signal,
} from '@angular/core'
import { Router } from '@angular/router'
import { environment } from '@environments/environment'
import { UserInterface } from '@ui-core/interfaces/user.interface'
import { MenuService } from '@ui-core/services/menu.service'
import { RouteService } from '@ui-core/services/route.service'
import { MenuItem } from 'primeng/api'
import { ButtonModule } from 'primeng/button'
import { TooltipModule } from 'primeng/tooltip'

@Component({
    selector: 'app-side-menu',
    imports: [ButtonModule, CommonModule, TooltipModule],
    templateUrl: './side-menu.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideMenu {
    private routeService = inject(RouteService)
    private router = inject(Router)
    private menuService = inject(MenuService)

    env = environment.env

    hideLogout = input<boolean>(false)
    isAdmin = input<boolean>(false)
    menuItems = model<MenuItem[]>()
    userInfo = input<UserInterface | null>(null)
    items = signal<MenuItem[]>([])

    logout = output<void>()
    collapsedChange = output<boolean>()

    isSideMenuVisible = signal(false)
    isCollapsed = signal(false)
    menuActive = signal('')

    name = computed(() => {
        const user = this.userInfo()

        if (!user) return ''

        return typeof user.name !== 'undefined' ? user.name.split(' ')[0] : user.preferredUsername
    })

    constructor() {
        effect(() => {
            const routeData = this.routeService.getData()() as Record<string, unknown> | undefined
            const menuActive = routeData?.['menuActive'] as string | undefined
            const expandMenu = routeData?.['expandMenu'] === true

            if (menuActive) {
                this.menuActive.set(menuActive)
            }

            // Auto-expand/collapse based on route config
            this.menuService.updateCollapseForRoute(expandMenu)
        })

        effect(() => {
            const isSideMenuVisible = this.menuService.getIsSideMenuVisible()()
            this.isSideMenuVisible.set(isSideMenuVisible)
        })

        effect(() => {
            const isCollapsed = this.menuService.getIsCollapsed()()
            this.isCollapsed.set(isCollapsed)
            this.collapsedChange.emit(isCollapsed)
        })

        effect(() => {
            const menu = this.menuService.get()()
            this.items.set(menu)
        })

        effect(() => {
            const items = this.menuItems()
            this.menuService.setMenuItemsCount(items?.length ?? 0)
        })
    }

    toggleSideMenu(): void {
        this.isSideMenuVisible.set(!this.isSideMenuVisible())
        this.menuService.setIsSideMenuVisible(!this.menuService.getIsSideMenuVisible()())
    }

    toggleCollapse(): void {
        this.menuService.toggleCollapsed()
    }

    goTo(url: string): void {
        this.isSideMenuVisible.set(false)
        this.menuService.setIsSideMenuVisible(false)
        this.router.navigate([url])
    }
}
